import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { DsBoxModule, DsButtonModule, DsMessageModule, DsTooltipDirectiveModule } from '@bmw-ds/components';

import { CustomerContact } from '../../../../../customer/model/customer-information.model';
import { FoundCustomer } from '../../../../../customer/model/found-customer.model';
import { useI18n } from '../../../../../i18n/services/i18n.service';
import {
  SyncStatus,
  SyncStatusComponent,
} from '../../../../../profile-page/components/sync-status/sync-status.component';
import { SearchResultMode } from '../../model/search-result-mode.model';
import { SearchResultLineComponent } from './search-result-line.component';

type CustomerSearchImportState = 'READONLY' | 'IMPORT';

@Component({
  selector: 'cdf-search-result',
  imports: [
    DsBoxModule,
    DsButtonModule,
    DsTooltipDirectiveModule,
    DsMessageModule,
    SyncStatusComponent,
    SearchResultLineComponent,
  ],
  template: `
    @if (customerSearchImportState() === 'READONLY') {
      <ds-message [messageText]="i18n('readonlyTooltip')" tone="neutral" variant="slim" class="pb-4x" />
    } @else if (customerSearchImportState() === 'IMPORT') {
      <ng-template #dsMessageContent>
        {{ i18n('importToRetail.info') }}
        <a class="hyperlink" (click)="openCustomerProfile.emit()">
          {{ i18n('importToRetail.link') }}
        </a>
      </ng-template>
      <ds-message [messageText]="dsMessageContent" tone="critical" variant="slim" class="pb-4x" />
    }

    <cdf-search-result-line [customer]="customer()" />
    <div class="button-section">
      <cdf-sync-status [syncStatus]="syncStatus()" />
      <ds-button
        ds-loading
        variant="ghost"
        icon="arrow_right_small"
        [disabled]="button().disabled"
        [isLoading]="isFoundCustomerLoading()"
        [ds-tooltip]="button().tooltip"
        (click)="openCustomerProfile.emit()"
        >{{ button().text }}</ds-button
      >
    </div>
  `,
  styleUrl: './search-result.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultComponent {
  protected readonly i18n = useI18n();

  readonly customer = input.required<FoundCustomer>();
  readonly searchResultMode = input<SearchResultMode>(SearchResultMode.CUSTOMER_SEARCH);
  readonly readonly = input(false);

  /**
   * Describes which customers profile is currently being opened and should display a loading state
   */
  readonly loadingCustomer = input<FoundCustomer | undefined>();
  readonly openCustomerProfile = output();

  protected readonly preferredContact = computed<CustomerContact>(() => ({
    address: this.customer().address,
    email: this.customer().email,
    phone: this.customer().phone,
    mobile: this.customer().mobile,
    bmwMiniId: this.customer().bmwMiniId,
  }));

  protected readonly useImportBanner = computed(() => this.searchResultMode() !== SearchResultMode.MERGE_WITH_NSC);

  protected readonly button = computed(() => {
    const textKey = [SearchResultMode.MERGE_WITH_NSC, SearchResultMode.C2V_LINK].includes(this.searchResultMode())
      ? this.i18n('searchResults.selectCustomer')
      : this.i18n('searchResults.openButton');
    const showBanner = this.useImportBanner() && !this.customer().accountNumber;
    return {
      text: textKey,
      tooltip: showBanner ? this.i18n('importToRetail.info') : '',
      disabled: showBanner,
    };
  });

  /**
   * The state of the banner/button to import the customer from ESO.
   *
   * @returns READONLY if an import is needed but the user only has read permissions,
   *          IMPORT if an import is needed and possible
   *          null if no import is needed/offered
   */
  protected readonly customerSearchImportState = computed((): CustomerSearchImportState | null => {
    if (this.useImportBanner() && !this.customer().accountNumber) {
      // The banner is only shown for customers without an account number in the customer search context

      if (this.readonly()) {
        return 'READONLY';
      } else {
        return 'IMPORT';
      }
    }
    return null;
  });

  // Indicate loading if gcid or accountNumber is matching with foundCustomer of this searchResult
  protected readonly isFoundCustomerLoading = computed(() => {
    const { gcid, accountNumber } = this.loadingCustomer() || {};
    const customer = this.customer();

    return (!!gcid && gcid === customer.gcid) || (!!accountNumber && accountNumber === customer.accountNumber);
  });

  /**
   * Same rule as the profile page ({@link ProfileSyncComponent}): a Retail CRM account counts as
   * linked to the NSC Database only while it is in sync. A GCID alone is not enough - after the
   * C2D was terminated the account keeps its GCID but is no longer in sync (CUSTAPP-783).
   */
  readonly syncStatus = computed((): SyncStatus => {
    const { accountNumber, inSyncWithDirectSales } = this.customer();
    if (!accountNumber) {
      return 'NSC_ONLY';
    }
    return inSyncWithDirectSales ? 'NSC_RETAIL_SYNCED' : 'RETAIL_ONLY';
  });
}
